import { Fragment } from 'react';
import { Helmet } from 'react-helmet';
import Image from 'components/Image';
import Footer from 'components/Footer';
import { Button } from 'components/Button';
import {
  ProjectContainer,
  ProjectBackground,
  ProjectHeader,
  ProjectSection,
  ProjectSectionContent,
  ProjectImage,
  ProjectSectionHeading,
  ProjectSectionText,
  ProjectSectionColumns,
  ProjectTextRow,
} from 'components/ProjectLayout';
import { useScrollRestore } from 'hooks';
import { media } from 'utils/style';
import prerender from 'utils/prerender';
import bjshBackground from 'assets/bjsh/bjsh-background.jpg';
import bjshBackgroundLarge from 'assets/bjsh/bjsh-background-large.jpg';
import bjshBackgroundPlaceholder from 'assets/bjsh/bjsh-background-placeholder.jpg';
import bjsh from 'assets/bjsh/bjsh.jpg';
import bjshLarge from 'assets/bjsh/bjsh-large.jpg';
import bjshPlaceholder from 'assets/bjsh/bjsh-placeholder.jpg';
import bjshBranding from 'assets/bjsh/bjsh-logo.jpg';
import bjshBrandingLarge from 'assets/bjsh/bjsh-logo-large.jpg';
import bjshBrandingPlaceholder from 'assets/bjsh/bjsh-logo-placeholder.jpg';
import bjshCodeBase from 'assets/bjsh/bjsh-code-base.jpg';
import bjshCodeBaseLarge from 'assets/bjsh/bjsh-code-base-large.jpg';
import bjshCodeBasePlaceholder from 'assets/bjsh/bjsh-code-base-placeholder.jpg';

const title = "Bon & Jason's Shell (BJSH)";
const description =
  'BJSH is a *NIX shell providing an interactive command-line interface similar to other Unix-like shells. (e.g., bash, zsh)';
const roles = [
  'C Programming',
  'Shell Development',
  'System Programming',
  'Command-line Interface',
];

const ProjectBJSH = () => {
  useScrollRestore();

  return (
    <Fragment>
      <Helmet>
        <title>Projects | {title}</title>
        <meta name="description" content={description} />
      </Helmet>
      <ProjectContainer>
        <ProjectBackground
          srcSet={`${bjshBackground} 1080w, ${bjshBackgroundLarge} 2160w`}
          placeholder={bjshBackgroundPlaceholder}
          entered={!prerender}
        />
        <ProjectHeader
          title={title}
          description={description}
          url="https://github.com/pewriebontal/bjsh"
          roles={roles}
        />{' '}
        <ProjectSection first>
          <ProjectSectionContent>
            <ProjectImage
              raised
              srcSet={`${bjsh} 1280w, ${bjshLarge} 2560w`}
              placeholder={bjshPlaceholder}
              sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 800px, 1000px`}
              alt="Bon & Jason's Shell (BJSH) running in a terminal."
            />
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection light>
          <ProjectTextRow>
            <ProjectSectionHeading>Introduction</ProjectSectionHeading>
            <ProjectSectionText>
              BJSH (Bon & Jason's Shell) is a custom-built *NIX shell program developed
              from scratch as part of the 42 curriculum. This lightweight, interactive
              command-line interface replicates core functionalities of popular Unix-like
              shells (e.g., bash, zsh) while maintaining efficiency and simplicity.
            </ProjectSectionText>
          </ProjectTextRow>
        </ProjectSection>
        <ProjectSection>
          <ProjectSectionColumns>
            <ProjectTextRow>
              <ProjectSectionHeading>
                The birth of Bon & Jason's Shell (BJSH)!
              </ProjectSectionHeading>
              <ProjectSectionText>
                Initially, Bon and Jason, two friends with a shared love for coding and
                memes, considered creating a programming language called "Bon and Jason
                Programming Language" with the file extension .bj.
              </ProjectSectionText>
              <ProjectSectionText>
                However, upon discovering the Minishell project at 42, they decided to
                collaborate on creating their own shell instead, naming it "Bon and
                Jason's Shell" or BJSH for short.
              </ProjectSectionText>
            </ProjectTextRow>
            <Image
              srcSet={`${bjshCodeBase} 400w, ${bjshCodeBaseLarge} 898w`}
              placeholder={bjshCodeBasePlaceholder}
              sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 100vw, 50vw`}
              alt="BJSH project opened in Visual Studio Code."
            />
          </ProjectSectionColumns>
        </ProjectSection>
        <ProjectSection light>
          <ProjectSectionContent>
            <ProjectTextRow>
              <ProjectSectionHeading>Features</ProjectSectionHeading>
              <ProjectSectionText>
                <li>
                  Command Execution: Executes commands using PATH variables or
                  absolute/relative paths
                </li>
                <li>
                  Input/Output Redirection: Supports &lt;, &gt;, &lt;&lt;, and &gt;&gt;
                  operators
                </li>
                <li>Piping: Implements | for connecting multiple commands</li>
                <li>
                  Environment Variables: Handles environment variables and $? for last
                  exit status
                </li>
                <li>Signal Handling: Custom handling for Ctrl-C, Ctrl-D, and Ctrl-\</li>
                <li>Quote Handling: Correctly interprets single and double quotes</li>
                <li>
                  Built-in Commands: Implements echo, cd, pwd, export, unset, env, and
                  exit
                </li>
                <li>Command History: Maintains a history of executed commands</li>
                <li>Here Documents: Supports here-doc functionality</li>
              </ProjectSectionText>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection>
          <ProjectSectionContent>
            <ProjectTextRow center centerMobile noMargin>
              <Image
                srcSet={`${bjshBranding} 180w, ${bjshBrandingLarge} 320w`}
                placeholder={bjshBrandingPlaceholder}
                alt="BJSH logo."
                sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 100vw, 220px`}
                style={{ maxWidth: 220, width: '100%', marginBottom: 30 }}
              />
              <ProjectSectionHeading>The Result</ProjectSectionHeading>
              <ProjectSectionText>
                Developing BJSH was a challenging yet rewarding experience that
                significantly improved my systems programming skills. Working on BJSH
                alongside Jason was an incredible experience. We learned so much about
                tokenization, processes, file descriptors, and system calls in C. We made
                sure to follow all the project guidelines, avoid crashes and memory leaks
                by managing memory properly, and maintain good coding practices.
              </ProjectSectionText>
              <ProjectSectionText>
                We successfully met all the requirements, and BJSH turned out to be a
                solid shell. This project sharpened our technical skills and proved we can
                handle complex programming tasks together. BJSH is a proud addition to our
                portfolios.
              </ProjectSectionText>
              <Button
                secondary
                iconHoverShift
                icon="chevronRight"
                href="https://github.com/pewriebontal/bjsh"
              >
                View on Github
              </Button>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
      </ProjectContainer>
      <Footer />
    </Fragment>
  );
};

export default ProjectBJSH;
